import { ensureFreshSession, refreshOnce } from "@auth/refreshMiddleware";
import { useSessionStore } from "@auth/sessionStore";
import {
  publishFrame,
  publishReconnect,
} from "@notifications/notificationFrames";
import { toRevision } from "@phone/phoneCallsClient";
import { phoneCallsKeys, refreshPhoneCall } from "@phone/phoneCallsQueries";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";

import { getConfig } from "../config";

const WS_URL = `${getConfig().wsUrl}/ws/notifications`;
const BEARER_SUBPROTOCOL = "scribenote.auth.bearer.v1";

const BASE_BACKOFF_MS = 1_000;
const MAX_BACKOFF_MS = 30_000;
const CLOSE_AUTH_REQUIRED = 1008;

type EntityType =
  | "email_message"
  | "task"
  | "artifact"
  | "capture_node"
  | "client"
  | "patient"
  | "room"
  | "visit"
  | "visit_stage"
  | "phone_call"
  | "phone_call_suggestions"
  | "phone_call_schedule"
  | "user"
  | "view";

interface NotificationFrame {
  events?: {
    type?: string;
    uuid?: string;
    label?: string;
    status?: string;
    /** brian sends a string; see `NotificationEvent`. */
    revision?: number | string;
    disclosure_state?: string;
    hold_state?: string;
    held_at?: string | null;
  }[];
}

const ENTITY_SEARCH_PREFIXES = ["entity-search"];

// Membership here is also the frame allowlist.
const PREFIXES_BY_TYPE: Record<EntityType, readonly string[]> = {
  // Inbox sync broadcasts one frame per newly imported incoming message
  // (brian `modules/email/inbox_sync.py`), clinic-wide.
  email_message: ["email"],
  task: ["tasks", "task"],
  artifact: ["artifacts"],
  // Queued on node transitions (brian `modules/capture_node/service.py`).
  capture_node: ["capture"],
  client: ENTITY_SEARCH_PREFIXES,
  patient: [...ENTITY_SEARCH_PREFIXES, "patients", "client-patients"],
  room: ENTITY_SEARCH_PREFIXES,
  visit: ["visits"],
  visit_stage: ["visits"],
  // Framed on every row write, so patched per row (`refreshPhoneCall`), never invalidated.
  phone_call: [],
  // Subscribers refresh only the affected call suggestion snapshot.
  phone_call_suggestions: [],
  // Likewise: the scheduling bubble re-reads its call's snapshot.
  phone_call_schedule: [],
  user: ENTITY_SEARCH_PREFIXES,
  // The Subject summary is a plain view, so this is also its only refresh.
  view: ["views", "view"],
};

export const useNotificationsSocket = () => {
  const queryClient = useQueryClient();
  const userUuid = useSessionStore((s) => s.user?.userUuid ?? null);

  useEffect(() => {
    if (!userUuid) return;

    let ws: WebSocket | null = null;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    let attempt = 0;
    let opened = false;
    let disposed = false;

    const handleFrame = (raw: string) => {
      let frame: NotificationFrame;
      try {
        frame = JSON.parse(raw);
      } catch {
        return;
      }
      const events = (frame.events ?? []).filter(
        (
          e,
        ): e is {
          type: EntityType;
          uuid?: string;
          label?: string;
          status?: string;
          revision?: number | string;
          disclosure_state?: string;
          hold_state?: string;
          held_at?: string | null;
        } =>
          !!e.type &&
          Object.prototype.hasOwnProperty.call(PREFIXES_BY_TYPE, e.type),
      );

      const prefixes = new Set<string>();
      for (const event of events) {
        for (const key of PREFIXES_BY_TYPE[event.type]) prefixes.add(key);
      }
      for (const prefix of prefixes) {
        void queryClient.invalidateQueries({ queryKey: [prefix] });
      }
      for (const event of events) {
        if (event.type === "phone_call" && event.uuid)
          refreshPhoneCall(queryClient, event.uuid);
      }
      // After the invalidations, so a subscriber's refetch reads a cache that
      // is already marked stale rather than racing the frame that made it so.
      publishFrame(
        events.map((e) => ({
          ...e,
          revision: toRevision(e.revision) ?? undefined,
        })),
      );
    };

    function scheduleReconnect() {
      if (disposed || reconnectTimer) return;
      const delay = Math.min(BASE_BACKOFF_MS * 2 ** attempt, MAX_BACKOFF_MS);
      attempt += 1;
      reconnectTimer = setTimeout(() => {
        reconnectTimer = null;
        void connect();
      }, delay);
    }

    async function connect() {
      if (disposed) return;
      const session = await ensureFreshSession();
      if (disposed) return;
      if (!session || session.scope !== "user") {
        scheduleReconnect();
        return;
      }

      const socket = new WebSocket(WS_URL, [
        BEARER_SUBPROTOCOL,
        session.accessToken,
      ]);
      ws = socket;

      socket.onopen = () => {
        attempt = 0;
        // Frames missed while down are never replayed: a reconnect re-reads the log.
        if (opened) {
          void queryClient.invalidateQueries({ queryKey: phoneCallsKeys.all });
          void queryClient.invalidateQueries({ queryKey: ["visits"] });
        }
        opened = true;
        // Fires on the first connect too: a subscriber re-reading everything
        // it tracks on a connect that lost nothing is a harmless no-op.
        publishReconnect();
      };

      socket.onmessage = (e) => {
        if (typeof e.data === "string") handleFrame(e.data);
      };

      socket.onerror = () => {
        socket.close();
      };

      socket.onclose = (e) => {
        if (ws === socket) ws = null;
        if (disposed) return;
        if (e.code === CLOSE_AUTH_REQUIRED) {
          void refreshOnce().then((rotated) => {
            if (disposed || !rotated) return;
            scheduleReconnect();
          });
          return;
        }
        scheduleReconnect();
      };
    }

    void connect();

    return () => {
      disposed = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      if (ws) {
        ws.onopen = ws.onmessage = ws.onerror = ws.onclose = null;
        ws.close();
      }
    };
  }, [userUuid, queryClient]);
};
